import React, { useState, useEffect, useRef } from "react";
import { Search, Clock, X } from "lucide-react";
import { fetchTargets, fetchSources } from "../../api/leaks";
import type { FilterState } from "./FeedFilters";

interface KeywordSearchProps {
  filters: FilterState;
  onChange: (filters: FilterState) => void;
  localSearch: string;
  setLocalSearch: (val: string) => void;
  setHasUnapplied?: (val: boolean) => void;
}

interface Suggestion {
  type: string;
  id: string;
  label: string;
}

export function KeywordSearch({ filters, onChange, localSearch, setLocalSearch, setHasUnapplied }: KeywordSearchProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [suggestions, setSuggestions] = useState<Suggestion[]>([]);
  const [loading, setLoading] = useState(false);
  const [highlightIndex, setHighlightIndex] = useState(-1);
  const [recentSearches, setRecentSearches] = useState<string[]>([]);
  
  const abortControllerRef = useRef<AbortController | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    try {
      const stored = localStorage.getItem("threatactix_recent_searches");
      if (stored) setRecentSearches(JSON.parse(stored));
    } catch (e) {}
  }, []);

  const saveRecentSearch = (term: string) => {
    if (!term.trim()) return;
    const next = [term, ...recentSearches.filter(s => s !== term)].slice(0, 5);
    setRecentSearches(next);
    localStorage.setItem("threatactix_recent_searches", JSON.stringify(next));
  };

  const clearRecent = () => {
    setRecentSearches([]);
    localStorage.removeItem("threatactix_recent_searches");
  };

  useEffect(() => {
    if (!isOpen || localSearch.length < 2) {
      setSuggestions([]);
      return;
    }

    if (abortControllerRef.current) abortControllerRef.current.abort();
    abortControllerRef.current = new AbortController();
    const { signal } = abortControllerRef.current;

    setLoading(true);
    const delay = setTimeout(async () => {
      try {
        const [orgs, sources, countries, industries, sites, regions] = await Promise.all([
          fetchTargets("Organization", localSearch, signal),
          fetchSources({ search: localSearch }),
          fetchTargets("Country", localSearch, signal),
          fetchTargets("Industry", localSearch, signal),
          fetchTargets("Site", localSearch, signal),
          fetchTargets("Region", localSearch, signal)
        ]);

        if (!signal.aborted) {
          const results: Suggestion[] = [];
          if (orgs) orgs.slice(0, 3).forEach((o: any) => results.push({ type: "Organization", id: o.id, label: o.label }));
          if (industries) industries.slice(0, 3).forEach((i: any) => results.push({ type: "Industry", id: i.id, label: i.label }));
          if (sources) sources.slice(0, 3).forEach((s: any) => results.push({ type: "Source", id: s.id, label: s.label }));
          if (sites) sites.slice(0, 3).forEach((s: any) => results.push({ type: "Site", id: s.id, label: s.label }));
          if (countries) countries.slice(0, 3).forEach((c: any) => results.push({ type: "Country", id: c.id, label: c.label }));
          if (regions) regions.slice(0, 3).forEach((r: any) => results.push({ type: "Region", id: r.id, label: r.label }));
          
          setSuggestions(results);
          setHighlightIndex(-1);
        }
      } catch (e) {}
      if (!signal.aborted) setLoading(false);
    }, 250);

    return () => {
      clearTimeout(delay);
      abortControllerRef.current?.abort();
    };
  }, [localSearch, isOpen]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const applySearch = (term: string) => {
    saveRecentSearch(term);
    onChange({ ...filters, search: term || undefined });
    setIsOpen(false);
  };

  const applySuggestion = (s: Suggestion) => {
    setLocalSearch("");
    if (s.type === "Source") {
      const current = filters.threatActor ? filters.threatActor.split(",") : [];
      if (!current.includes(s.id)) current.push(s.id);
      onChange({ ...filters, threatActor: current.join(","), search: undefined });
    } else {
      const targets = filters.targets ? JSON.parse(filters.targets) : {};
      if (!targets[s.type]) targets[s.type] = [];
      if (!targets[s.type].includes(s.id)) targets[s.type].push(s.id);
      onChange({ ...filters, targets: JSON.stringify(targets), search: undefined });
    }
    setIsOpen(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!isOpen) return;
    const max = localSearch.length < 2 ? recentSearches.length : suggestions.length;
    
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlightIndex(i => (i < max - 1 ? i + 1 : i));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlightIndex(i => (i > -1 ? i - 1 : -1));
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (highlightIndex >= 0) {
        if (localSearch.length < 2) {
          setLocalSearch(recentSearches[highlightIndex]);
          applySearch(recentSearches[highlightIndex]);
        } else {
          applySuggestion(suggestions[highlightIndex]);
        }
      } else {
        applySearch(localSearch);
      }
    } else if (e.key === "Escape") {
      setIsOpen(false);
    }
  };

  return (
    <div ref={containerRef} className="relative w-full h-12 flex items-center bg-transparent border border-slate-700/50 rounded-lg hover:border-slate-500 focus-within:border-indigo-500 transition-colors z-30">
      <Search size={16} className="absolute left-3 text-slate-500 pointer-events-none" />
      <input
        ref={inputRef}
        type="text"
        aria-label="Search organizations, leaked data, technologies, or threats"
        className="w-full h-full pl-9 pr-9 text-sm bg-transparent outline-none text-white placeholder-slate-500 rounded-lg"
        placeholder="Search organizations, leaked data, technologies, or threats..."
        value={localSearch}
        onFocus={() => setIsOpen(true)}
        onChange={(e) => {
          setLocalSearch(e.target.value);
          if (setHasUnapplied && e.target.value !== (filters.search || "")) {
            setHasUnapplied(true);
          }
          if (!isOpen) setIsOpen(true);
        }}
        onKeyDown={handleKeyDown}
      />
      {localSearch && (
        <button
          type="button"
          aria-label="Clear search input"
          className="absolute right-2.5 p-1 text-slate-400 hover:text-white rounded-md transition-colors"
          onClick={(e) => {
            e.stopPropagation();
            setLocalSearch("");
            onChange({ ...filters, search: undefined });
            if (setHasUnapplied) setHasUnapplied(false);
            inputRef.current?.focus();
          }}
        >
          <X size={15} />
        </button>
      )}
      
      {isOpen && (
        <div className="absolute top-14 left-0 right-0 bg-slate-900 border border-slate-700 rounded-lg shadow-xl overflow-hidden">
          {localSearch.length < 2 ? (
            <div className="p-2">
              <div className="flex items-center justify-between px-2 pb-2 text-xs text-slate-500 uppercase font-medium">
                <span>Recent Searches</span>
                {recentSearches.length > 0 && <button onClick={clearRecent} className="hover:text-slate-300">Clear</button>}
              </div>
              {recentSearches.length === 0 ? (
                <div className="px-2 py-3 text-sm text-slate-400">No recent searches</div>
              ) : (
                recentSearches.map((s, i) => (
                  <button
                    key={s}
                    className={`w-full flex items-center gap-2 px-2 py-2 text-sm rounded text-left transition-colors ${i === highlightIndex ? "bg-slate-800 text-indigo-400" : "text-slate-300 hover:bg-slate-800/50"}`}
                    onClick={() => {
                      setLocalSearch(s);
                      applySearch(s);
                    }}
                  >
                    <Clock size={14} className="text-slate-500" />
                    {s}
                  </button>
                ))
              )}
              
              <div className="mt-2 pt-2 border-t border-slate-700/50 px-2 py-1 text-xs text-slate-500">
                <p className="mb-1">Syntax help:</p>
                <ul className="list-disc pl-4 space-y-0.5">
                  <li><code className="bg-slate-800 px-1 rounded text-slate-300">"login details"</code> exact phrase</li>
                  <li><code className="bg-slate-800 px-1 rounded text-slate-300">login -details</code> exclude word</li>
                  <li><code className="bg-slate-800 px-1 rounded text-slate-300">example[.]com</code> defanged domains</li>
                </ul>
              </div>
            </div>
          ) : (
            <div className="p-2">
              <div className="px-2 pb-2 text-xs text-slate-500 uppercase font-medium">
                Suggestions
                {loading && <span className="ml-2 lowercase text-slate-600">...</span>}
              </div>
              {suggestions.length === 0 && !loading ? (
                <div className="px-2 py-3 text-sm text-slate-400">No suggestions, press Enter to search</div>
              ) : (
                suggestions.map((s, i) => (
                  <button
                    key={`${s.type}-${s.id}`}
                    className={`w-full flex flex-col px-3 py-2 text-sm rounded text-left transition-colors ${i === highlightIndex ? "bg-slate-800" : "hover:bg-slate-800/50"}`}
                    onClick={() => applySuggestion(s)}
                  >
                    <span className={i === highlightIndex ? "text-indigo-400" : "text-slate-300"}>{s.label}</span>
                    <span className="text-xs text-slate-500">{s.type}</span>
                  </button>
                ))
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
